import React, { FC, useState } from 'react';

import { Box, Center, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { each, map, toArray } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { selectCompliance } from 'app/containers/Compliance/selectors';
import { actions } from 'app/containers/Compliance/slice';
import {
  CustomTooltip,
  formatNumberWithComma,
  getIcon,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import {
  ResourceTypeIconTooltip,
  useResourceType,
} from 'containers/App/hooks/useResourceType';
import { AddException } from 'containers/Compliance/Components/AddException';
import {
  renderPassedChecks,
  renderViolations,
} from 'containers/Compliance/Components/ComplianceTables/utils';
import { History } from 'containers/Compliance/Components/History';
import { useCompliance } from 'containers/Compliance/Components/hooks/context';
import { SeverityComponent } from 'containers/Compliance/Components/SeverityComponent';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

import { ByResourceTypeSubComponent } from './ByResourceTypeSubComponent';

export const ResourceTable: FC = () => {
  const { byResourceType } = useSelector(selectCompliance);

  const {
    resourceTypeRowIndex,
    renderRulesActions,
    isOpenSeverityModal,
    isOpenSuppress,
    onCloseSuppress,
    selectedResource,
    isOpenHistory,
  } = useCompliance();

  const dispatch = useDispatch();
  const { accountMapping } = useCloudAccountId();
  const { getCloudNativeName, getCloudAgnosticName } = useResourceType();
  const resources = useDisclosure();
  const [currentRecord, setCurrentRecord] = useState<Record<string, any>>({});

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Entity Type</Box>,
      accessor: 'type',
      align: 'left',
      render: ({ value }) => (
        <WithResourceIcon
          resourceType={value}
          iconTooltip={<ResourceTypeIconTooltip resourceType={value} />}
          iconSize="9"
        >
          <StackedCell
            upper={getCloudNativeName(value)}
            lower={getCloudAgnosticName(value)}
          />
        </WithResourceIcon>
      ),
    },
    {
      header: 'Entity Count',
      accessor: 'check_stats.total_entities',
      render: ({ value }) => formatNumberWithComma(value),
      styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
    {
      header: 'Accounts',
      accessor: 'account_ids',
      render: ({ value }) => {
        let clouds = new Set();
        each(value, o => clouds.add(accountMapping[o]?.cloud_type));
        return (
          <CustomTooltip
            label={
              <Stack>
                {map(value, o => (
                  <Box>
                    {accountMapping[o]?.name ??
                      accountMapping[o]?.cloud_account_id}
                  </Box>
                ))}
              </Stack>
            }
          >
            <Center>
              <Stack spacing={0}>
                <HStack>
                  {each(toArray(clouds) as string[], c => (
                    <Box boxSize={4}>
                      <Center>{getIcon(c)}</Center>
                    </Box>
                  ))}
                </HStack>
                <Box>
                  {(value?.length ?? 0) +
                    ' account' +
                    (value?.length === 1 ? '' : 's')}
                </Box>
              </Stack>
            </Center>
          </CustomTooltip>
        );
      },
    },
    {
      header: 'Enforced Policies',
      accessor: 'rules.total',
    },
    {
      header: 'Exceptions',
      accessor: 'check_stats.resource_exceptions',
    },
    {
      header: 'Violations',
      accessor: 'check_stats',
      render: renderViolations,
      align: 'left',
    },
    {
      header: 'Passed Policy Checks',
      accessor: 'check_stats',
      render: renderPassedChecks,
      styles: { width: '250px', cell: { maxWidth: '250px' } },
    },
    {
      header: 'Actions',
      accessor: '',
      render: renderRulesActions,
      styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
  ];

  return (
    <Stack h="full" spacing={4}>
      <Table
        data={byResourceType.data}
        columns={columns}
        cursor="pointer"
        isLoading={byResourceType.isLoading}
        onRowClick={row => {
          resources.onOpen();
          setCurrentRecord(row);
        }}
        rowIndex={resourceTypeRowIndex}
        pagination={{
          totalCount: byResourceType.page.totalCount,
          pageInfo: byResourceType.page.info,
          onChange: pageInfo => {
            dispatch(actions.updateByResourceTypePageInfo(pageInfo));
          },
        }}
      />
      {resources.isOpen && (
        <ByResourceTypeSubComponent
          row={currentRecord}
          isOpen={resources.isOpen}
          onClose={resources.onClose}
        />
      )}
      {isOpenHistory && <History />}
      {isOpenSeverityModal && <SeverityComponent />}
      {isOpenSuppress && (
        <AddException
          resource={selectedResource}
          isOpen={isOpenSuppress}
          onClose={onCloseSuppress}
        />
      )}
    </Stack>
  );
};
